import { ActiveCorners, DefaultHeights } from "../components/process-design/corner/CornerConstants";
import api from "./api";
import { produce } from "immer";
import { isDigit, setProperty } from "../utils/util";
import { hfInstance } from "./Cells";

const defaultColumns = {
  Header: { show: "0" },
  Show: { show: "0" },
  Size: { show: "0" },
  Blank: { show: "0" },
  Flow: { show: "0" },
};
const defaultRows = {
  Header: { show: "0" },
  Show: { show: "0" },
  Size: { show: "0" },
  Blank: { show: "0" },
};

const initialState = {
  allViews: [],
  activeView: {
    rows: {},
    columns: {},
  },
  activeCorner: null,
  showAddViewDialog: false,
  persistTimeout: null,
  editingViewId: null,
  isLoading: false,
  isViewVisible: false,
  showSeriesDialog: false,
};
export default {
  namespaced: true,
  state: () => ({ ...initialState }),
  mutations: {},
  actions: {
    setEditingViewID({ state }, id) {
      state.editingViewId = id;
    },
    toggleViewsVisibility({ state }) {
      state.isViewVisible = !state.isViewVisible;
    },
    setViewsVisibility({ state }, value) {
      state.isViewVisible = value;
    },
    setActiveCorner({ commit, state, dispatch }, data) {
      state.activeCorner = data;
      if (data === ActiveCorners.NavigationCorner) dispatch("cells/showNavigationCorner", null, { root: true });
      else dispatch("cells/hideNavigationCorner", null, { root: true });

      dispatch("customFlow/calculateCys", null, { root: true });
    },
    changeValue({ commit, state, dispatch }, { id, key, value, dimension }) {
      state.activeView = produce(state.activeView, (activeView) => {
        setProperty(activeView[dimension], id, key, value);
      });
      dispatch("persist");
      dispatch("customFlow/calculateCys", null, { root: true });
    },
    changeValues({ commit, state, dispatch }, { ids, key, value, dimension }) {
      state.activeView = produce(state.activeView, (activeView) => {
        for (let id of ids) {
          setProperty(activeView[dimension], id, key, value);
        }
      });
      dispatch("persist");
      dispatch("customFlow/calculateCys", null, { root: true });
    },
    setShowAddViewDialog({ commit, state, dispatch }, data) {
      state.showAddViewDialog = data;
    },
    setSeriesDialogVisibility({ commit, state, dispatch }, data) {
      state.showSeriesDialog = data;
    },
    async addView({ commit, state, rootState, dispatch }, { title, icon, designId, rows, columns }) {
      try {
        const sheetId = rootState.sheets.currentSheet.id;

        const view = (await api.post("views", { sheetId, title, icon, designId, rows, columns })).data;
        const views = (await api.get(`views/${designId}/get-sheet-views`, { params: { sheetId } })).data;
        state.allViews = produce(state.allViews, (allViews) => {
          allViews.push({ _id: view._id, title, icon, designId, sheetId });
        });

        state.activeView = view;
        if (views.length > 1) {
          dispatch(
            "addToast",
            {
              severity: "success",
              life: 3000,
              summary: "Created Successfully",
              detail: "View created Successfully.",
            },
            { root: true }
          );
        }
        dispatch("preferences/update", { key: "activeView", value: state.activeView }, { root: true });
        dispatch("updateCornerData");
        dispatch("persist");
        dispatch("customFlow/calculateCys", null, { root: true });
        dispatch("cells/hideNavigationCorner", null, { root: true });
        dispatch("smartSearch/loadViews", null, { root: true });
      } catch (error) {
        dispatch(
          "pdesign/setError",
          {
            show: true,
            type: "warning",
            summary: error?.response.data.errors.title.name,
            detail: error?.response.data.errors.title.message,
            buttons: [
              { text: "Cancel", type: "cancel" },
              {
                text: "Ok",
                type: "confirm",
                action: () => {
                  dispatch("views/setShowAddViewDialog", true, { root: true });
                },
              },
            ],
          },
          { root: true }
        );
        console.log("response", error?.response);
      }
    },
    async updateView({ commit, state, dispatch }, { id, title, icon, rows, columns }) {
      try {
        // Make the API call to update the view with the provided id
        await api.patch(`views/${id}`, { title, icon, id, rows, columns });
        dispatch(
          "addToast",
          {
            severity: "success",
            life: 3000,
            summary: "Updated Successfully",
            detail: "View Updated Successfully.",
          },
          { root: true }
        );
        // Fetch the updated list after deletion
        dispatch("load", state.activeView.design_id);
        dispatch("smartSearch/loadViews", null, { root: true });
      } catch (error) {
        dispatch(
          "pdesign/setError",
          {
            show: true,
            type: "warning",
            summary: "Error",
            detail: "Could not update the view, try again",
            buttons: [
              { text: "Cancel", type: "cancel" },
              { text: "Ok", type: "confirm" },
            ],
          },
          { root: true }
        );
        console.log("view error", error?.response);
      }
    },
    async remove({ commit, state, dispatch }, { ids }) {
      try {
        await api.delete(`views/${ids}`);
        dispatch(
          "addToast",
          {
            severity: "success",
            life: 3000,
            summary: "Deleted Successfully",
            detail: "View Deleted Successfullys.",
          },
          { root: true }
        );
        // Fetch the updated list after deletion
        await dispatch("load", state.activeView.design_id);
        dispatch("smartSearch/loadViews", null, { root: true });
      } catch (error) {
        dispatch(
          "pdesign/setError",
          {
            show: true,
            type: "warning",
            summary: error?.response.data.status,
            detail: error?.response.data.message,
            buttons: [
              { text: "Cancel", type: "cancel" },
              { text: "Ok", type: "confirm" },
            ],
          },
          { root: true }
        );
        console.log("response", error?.response);
      }
    },
    async load({ commit, state, dispatch, rootState }, designId) {
      const sheetId = rootState.sheets.currentSheet.id;
      const views = (await api.get(`views/${designId}/get-sheet-views`, { params: { sheetId } })).data;
      state.allViews = views;
      if (views.length === 0) {
        await dispatch("addView", {
          title: "Standard",
          icon: "icon-home",
          designId,
          rows: defaultRows,
          columns: defaultColumns,
        });
      } else {
        const prefActiveViewId = rootState.preferences.allPreferences.activeView?._id;
        const viewId = state.allViews.some((x) => x._id === prefActiveViewId)
          ? prefActiveViewId
          : state.allViews[0]._id;
        state.activeView = (await api.get(`views/${designId}/${viewId}`, { params: { sheetId } })).data;
        if (state.activeView._id !== prefActiveViewId)
          dispatch(
            "preferences/update",
            {
              key: "activeView",
              value: state.activeView,
            },
            { root: true }
          );
      }

      dispatch("updateCornerData");
      // Question Do we need to give condition if not grid?
      dispatch("customFlow/calculateCys", null, { root: true });
    },
    async setActiveView({ commit, state, dispatch, rootState }, id) {
      const designId = rootState.cells.current.id;
      const sheetId = rootState.sheets.currentSheet.id;
      try {
        state.activeView = (await api.get(`views/${designId}/${id}`, { params: { sheetId } })).data;

        if (state.activeView === ActiveCorners.NavigationCorner)
          dispatch("cells/showNavigationCorner", null, { root: true });
        else dispatch("cells/hideNavigationCorner", null, { root: true });

        dispatch("updateCornerData");
        dispatch("customFlow/calculateCys", null, { root: true });
        dispatch("preferences/update", { key: "activeView", value: state.activeView }, { root: true });
      } catch (error) {
        dispatch(
          "pdesign/setError",
          {
            show: true,
            type: "warning",
            summary: error?.response.data.status,
            detail: error?.response.data.message + ", try again",
            buttons: [
              { text: "Cancel", type: "cancel" },
              { text: "Ok", type: "confirm" },
            ],
          },
          { root: true }
        );
        console.log(error?.response);
      }
      state.isLoading = false;
    },
    async updateViewData({ commit, state, dispatch, rootState, rootGetters }, ids) {
      state.activeView = produce(state.activeView, (activeView) => {
        if (typeof ids !== "object") ids = [ids];
        ids.forEach((id) => {
          const [rowId, columnId] = id.split("_");
          const [columnIndex, rowIndex] = rootGetters["cells/getCellCoordinate"](id);
          const contentValue = rootGetters["cells/cellText"](rowIndex, columnIndex);

          if (rowId === "Show" || rowId === "Size") {
            if (!contentValue) {
              if (activeView.columns[columnId] && activeView.columns[columnId][rowId.toLowerCase()])
                delete activeView.columns[columnId][rowId.toLowerCase()];
            } else if (isDigit(contentValue)) {
              activeView.columns[columnId] = {
                ...(activeView.columns[columnId] || {}),
                [rowId.toLowerCase()]: rowId === "Size" ? Math.abs(contentValue) : contentValue,
              };
            }
          } else if (columnId === "Show" || columnId === "Size") {
            if (!contentValue) {
              if (activeView.rows[rowId] && activeView.rows[rowId][columnId.toLowerCase()])
                delete activeView.rows[rowId][columnId.toLowerCase()];
            } else if (isDigit(contentValue)) {
              activeView.rows[rowId] = {
                ...(activeView.rows[rowId] || {}),
                [columnId.toLowerCase()]: columnId === "Size" ? Math.abs(contentValue) : contentValue,
              };
            }
          }
        });
      });
      await dispatch("persist");
    },
    async changeRowsVisiblity({ commit, state, dispatch, rootState, rootGetters }, payload) {
      let { ids, contentValue } = payload;
      state.activeView = produce(state.activeView, (activeView) => {
        if (typeof ids !== "object") ids = [ids];
        ids.forEach((id) => {
          const [rowId, columnId] = [id, "show"];
          if (!contentValue) {
            if (activeView.rows[rowId] && activeView.rows[rowId][columnId.toLowerCase()])
              delete activeView.rows[rowId][columnId.toLowerCase()];
          } else if (isDigit(contentValue)) {
            activeView.rows[rowId] = {
              ...(activeView.rows[rowId] || {}),
              [columnId.toLowerCase()]: contentValue,
            };
          }
        });
      });
      await dispatch("persist");
    },
    async updateCornerData({ commit, state, dispatch, rootState }) {
      dispatch("cells/removeCornerData", { rowIds: ["Show", "Size"], columnIds: ["Show", "Size"] }, { root: true });
      const currentSheetId = rootState.sheets.currentSheet.sheetId;
      const rowSizeArray = [];
      const rowShowArray = [];
      Object.entries(state.activeView.columns).forEach(([key, value]) => {
        const index = rootState.cells.current.design.columnMap[key]?.index;
        if ("show" in value && index) {
          rowShowArray[index] = value.show;
        }
        if ("size" in value && index) {
          rowSizeArray[index] = value.size;
        }
      });

      const rowIndex = rootState.cells.current.design.rowMap["Show"].index;
      hfInstance.setCellContents({ sheet: currentSheetId, col: 0, row: rowIndex }, [rowShowArray, rowSizeArray]);

      const rowLength = rootState.cells.current.design.rows.length;
      const maxHeight =
        Object.keys(state.activeView.rows).reduce((acc, newValue) => {
          const index = rootState.cells.current.design.rowMap[newValue]?.index;
          return index > acc ? index : acc;
        }, 0) + 1;

      const columnData = Array.from(Array(maxHeight), () => ["", ""]);
      Object.entries(state.activeView.rows).forEach(([key, value]) => {
        const index = rootState.cells.current.design.rowMap[key]?.index;
        if ("show" in value && index) {
          columnData[index][0] = value.show;
        }
        if ("size" in value && index) {
          columnData[index][1] = value.size;
        }
      });

      const columnIndex = rootState.cells.current.design.columnMap["Show"].index;

      hfInstance.setCellContents({ sheet: currentSheetId, col: columnIndex, row: 0 }, columnData);
      hfInstance.setCellContents({ sheet: currentSheetId, row: 0, col: 1 }, [["Show", "Size"]]);
      hfInstance.setCellContents({ sheet: currentSheetId, row: 1, col: 0 }, [["Show"], ["Size"]]);
    },
    async persist({ commit, state, dispatch }) {
      clearTimeout(state.persistTimeout);
      state.persistTimeout = setTimeout(async () => {
        try {
          const id = state.activeView._id;
          await api.patch(`views/${id}`, state.activeView);
        } catch (error) {
          console.log(error);
        }
      }, 1000);
    },
    async resetState({ state }) {
      Object.assign(state, { ...initialState });
    },
  },
  getters: {
    rawHeight: (state) => (rowId) => {
      if (state.activeCorner) return DefaultHeights.Default;

      const height = state.activeView?.rows[rowId]?.size || DefaultHeights[rowId] || DefaultHeights.Default;
      return height;
    },
    rowVisibility: (state) => (rowId) => {
      if (state.activeCorner) return true;

      return !(state.activeView?.rows[rowId]?.show === "0");
    },
  },
};
